import React from 'react';

import { Code } from '@atlaskit/code';
import Heading from '@atlaskit/heading';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { Table, TBody, TD, TH, THead, TR } from '@atlaskit/table/primitives';
import { token } from '@atlaskit/tokens';

import HorizontalScrollContainer from '../horizontal-scroll-container';
import CollapsingTopMargin from '../utils/collapsing-top-margin';

const fontWeightMap = {
  '400': 'Regular',
  '500': 'Medium',
  '600': 'Semibold',
  '700': 'Bold',
};

type TypographyValue = {
  previewText: string;
  tokenName: string;
  propValue: string;
  fontSize: string;
  lineHeight: string;
  fontWeight: number;
  paragraphSpacing?: string;
};

type TypographyTableProps = {
  values: TypographyValue[];
  isMonospace?: boolean;
  hasParagraphSpacing?: boolean;
  testId?: string;
};

const monospaceStyles = xcss({
  font: token('font.code'),
  fontFamily: token('font.family.code'),
});

/**
 * __Typography table__
 */
export const TypographyTable = ({
  values,
  hasParagraphSpacing,
  testId,
}: TypographyTableProps) => {
  return (
    <CollapsingTopMargin>
      <HorizontalScrollContainer>
        <Table testId={testId}>
          <THead>
            <TR isBodyRow={false}>
              <TH width="100px">Preview</TH>
              <TH width="180px">Token</TH>
              <TH width="110px">Font weight</TH>
              <TH width="145px">Font size</TH>
              <TH width="130px">Line height</TH>
              {hasParagraphSpacing && <TH width="180px">Paragraph spacing*</TH>}
            </TR>
          </THead>
          <TBody>
            {values.map(token => {
              const { tokenName, previewText, propValue } = token;
              const isCode = tokenName.includes('code');
              const fontSizeValue = token.fontSize.slice(0, -2);
              const fontSizeString = isCode ? token.fontSize : `${Number(fontSizeValue) / 16} rem / ${fontSizeValue} px`;

              const lineHeightValue = token.lineHeight.slice(0, -2);
              const lineHeightString = isCode ? token.lineHeight : `${Number(lineHeightValue) / 16} rem / ${lineHeightValue} px`;

              const paragraphSpacingValue = token.paragraphSpacing ? token.paragraphSpacing.slice(0, -2) : undefined;
              const paragraphSpacingString = token.paragraphSpacing ? `${Number(paragraphSpacingValue) / 16} rem / ${paragraphSpacingValue} px` : undefined;

              const fontWeightValue =
                token.fontWeight.toString() as keyof typeof fontWeightMap;

              let textComponent;
              if (tokenName.includes('heading')) {
                // @ts-expect-error size prop value type is too broad
                textComponent = <Heading size={propValue}>{previewText}</Heading>
              } else if (tokenName.includes('body')) {
                // @ts-expect-error size prop value type is too broad
                textComponent = <Text size={propValue}>{previewText}</Text>
              } else if (tokenName.includes('code')) {
                textComponent = <Box as="span" xcss={monospaceStyles}>{previewText}</Box>
              }

              return (
                <TR key={tokenName}>
                  <TD>
                    {textComponent}
                  </TD>
                  <TD>
                    <Code>{tokenName}</Code>
                  </TD>
                  <TD>
                    <Text>
                      {token.fontWeight} / {fontWeightMap[fontWeightValue]}
                    </Text>
                  </TD>
                  <TD>
                    <Text>{fontSizeString}</Text>
                  </TD>
                  <TD>
                    <Text>{lineHeightString}</Text>
                  </TD>
                  {
                    hasParagraphSpacing &&
                    <TD>
                      <Text>{paragraphSpacingString}</Text>
                    </TD>
                  }
                </TR>
              );
            })}
          </TBody>
        </Table>
      </HorizontalScrollContainer>
    </CollapsingTopMargin>
  );
};
